// ============================================
// FILE: stateManagers/store/authStore.ts
import { create } from 'zustand';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';

// Minimal user info from JWT (only what's actually in the token)
export interface AuthUser {
  id: number;
  role: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  loading: boolean;
  setAuth: (auth: Partial<AuthState>) => void;
  clearAuth: () => void;
  initAuth: () => void;
}

// Helper: extract only id + role from JWT
const extractUserFromToken = (token: string): AuthUser | null => {
  try {
    const decoded = jwtDecode<{ user?: { id: number; role: string } }>(token);
    const user = decoded.user;
    if (!user) return null;
    return {
      id: user.id,
      role: user.role,
    };
  } catch (error) {
    console.error('❌ Failed to decode JWT:', error);
    return null;
  }
};

// Cookie options (secure in production)
const COOKIE_OPTIONS = {
  httpOnly: false,
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'Lax' as const,
  path: '/',
} as const;

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  user: null,
  accessToken: null,
  refreshToken: null,
  loading: true,

  setAuth: (auth) => {
    console.log('📝 authStore: Updating auth state:', auth);
    set((state) => ({ ...state, ...auth }));
  },

  clearAuth: () => {
    console.log('🧹 authStore: Clearing auth tokens');
    Cookies.remove('accessToken', COOKIE_OPTIONS);
    Cookies.remove('refreshToken', COOKIE_OPTIONS);
    set({
      isAuthenticated: false,
      user: null,
      accessToken: null,
      refreshToken: null,
      loading: false,
    });
  },

  initAuth: () => {
    console.log('🚀 authStore: Initializing from cookies...');
    try {
      const accessToken = Cookies.get('accessToken');
      const refreshToken = Cookies.get('refreshToken');

      if (accessToken && refreshToken) {
        const user = extractUserFromToken(accessToken);
        if (user) {
          console.log('✅ authStore: Restored session from JWT');
          set({
            isAuthenticated: true,
            user,
            accessToken,
            refreshToken,
            loading: false,
          });
        } else {
          console.warn('⚠️ authStore: Invalid JWT format');
          Cookies.remove('accessToken', COOKIE_OPTIONS);
          Cookies.remove('refreshToken', COOKIE_OPTIONS);
          set({ loading: false });
        }
      } else {
        console.log('ℹ️ authStore: No active session');
        set({ loading: false });
      }
    } catch (error) {
      console.error('❌ authStore: Init error:', error);
      Cookies.remove('accessToken', COOKIE_OPTIONS);
      Cookies.remove('refreshToken', COOKIE_OPTIONS);
      set({
        isAuthenticated: false,
        user: null,
        accessToken: null,
        refreshToken: null,
        loading: false,
      });
    }
  },
}));